import { FC, useEffect, useRef, useState } from 'react';

import { fetchGiftStoreBannersByBugo } from '@shared/api/giftStoreBanner/giftStoreBanner.controller';
import {
  GiftStoreBannerLinkType,
  GiftStoreBannerPublic,
} from '@shared/api/giftStoreBanner/giftStoreBanner.interface';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import giftPromStoreMain from 'src/assets/gift/gift-prom-store-main.png';

// 배너 비율 720×348 (기본 이미지·업로드 권장 규격 공통) — 로딩 중에도 자리를 잡아 목록이 밀리지 않게
const BANNER_RATIO_PADDING = `${(348 / 720) * 100}%`;
const AUTO_ADVANCE_MS = 5000;

// 스토어 안 이동(상세 → 뒤로가기)마다 다시 받지 않도록 부고별로 기억한다.
// null = 기본 배너(구서버 404·조회 실패·0건)
const bannerCache = new Map<string, GiftStoreBannerPublic[] | null>();

const bannerImageUrl = (banner: GiftStoreBannerPublic, variant = 'w1080'): string => {
  const image = banner.image;
  if (image && typeof image === 'object' && image.url) {
    return fileBucketUrl(image.url, variant);
  }
  return '';
};

const isClickable = (banner: GiftStoreBannerPublic) =>
  !!banner.link && banner.link.type !== GiftStoreBannerLinkType.None;

type Props = {
  bugoId: string;
  onSelect: (banner: GiftStoreBannerPublic) => void;
};

/**
 * 답례품 스토어 메인 배너 — 서버가 켜짐·기간·대상 상조로 걸러 준 배너를 순서대로 보여준다.
 * 배너가 없거나 서버가 아직 모르면(404) 기존 기본 이미지를 그대로 보여준다.
 */
export const GiftStoreBannerCarousel: FC<Props> = function GiftStoreBannerCarousel({
  bugoId,
  onSelect,
}) {
  const [banners, setBanners] = useState<GiftStoreBannerPublic[] | null | undefined>(
    () => (bannerCache.has(bugoId) ? bannerCache.get(bugoId) : undefined),
  );

  useEffect(() => {
    if (bannerCache.has(bugoId)) {
      setBanners(bannerCache.get(bugoId));
      return;
    }
    let cancelled = false;
    setBanners(undefined);
    fetchGiftStoreBannersByBugo(bugoId)
      .then((list) => {
        // 이미지 없는 문서는 그릴 수 없으니 뺀다
        const usable = (list ?? []).filter((b) => bannerImageUrl(b));
        return usable.length > 0 ? usable : null;
      })
      .catch((error) => {
        console.error('스토어 배너 조회 실패:', error);
        return null;
      })
      .then((result) => {
        bannerCache.set(bugoId, result);
        if (!cancelled) setBanners(result);
      });
    return () => {
      cancelled = true;
    };
  }, [bugoId]);

  const scrollerRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  // 손으로 한 번이라도 넘기면 자동 넘김을 멈춘다
  const [userTouched, setUserTouched] = useState(false);
  const count = banners?.length ?? 0;

  useEffect(() => {
    if (count <= 1 || userTouched) return;
    const timer = window.setInterval(() => {
      const el = scrollerRef.current;
      if (!el || document.hidden) return;
      const next = (Math.round(el.scrollLeft / el.clientWidth) + 1) % count;
      el.scrollTo({ left: next * el.clientWidth, behavior: 'smooth' });
    }, AUTO_ADVANCE_MS);
    return () => window.clearInterval(timer);
  }, [count, userTouched]);

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el || el.clientWidth === 0) return;
    setIndex(Math.min(count - 1, Math.max(0, Math.round(el.scrollLeft / el.clientWidth))));
  };

  // 로딩 중: 배너 자리만 잡아 둔다 (기본 이미지를 먼저 보였다 바꾸면 깜빡임)
  if (banners === undefined) {
    return (
      <div className="relative w-full bg-gray-100" style={{ paddingTop: BANNER_RATIO_PADDING }} />
    );
  }

  if (banners === null) {
    return (
      <div>
        <img src={giftPromStoreMain} alt="답례품 안내" className="w-full" />
      </div>
    );
  }

  return (
    <div className="relative w-full bg-gray-100" style={{ paddingTop: BANNER_RATIO_PADDING }}>
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        onTouchStart={() => setUserTouched(true)}
        onMouseDown={() => setUserTouched(true)}
        className="hide-scrollbar absolute inset-0 flex snap-x snap-mandatory overflow-x-auto"
      >
        {banners.map((banner, i) => {
          const image = (
            <img
              src={bannerImageUrl(banner)}
              alt={banner.alt || '답례품 안내'}
              className="h-full w-full object-cover"
              draggable={false}
              loading={i === 0 ? 'eager' : 'lazy'}
              onError={(e) => {
                // 리사이즈본이 없으면 원본으로 한 번만 재시도
                const img = e.currentTarget;
                if (img.dataset.fallback) return;
                img.dataset.fallback = '1';
                img.src = bannerImageUrl(banner, 'raw');
              }}
            />
          );
          return (
            <div key={banner._id} className="h-full w-full shrink-0 snap-center">
              {isClickable(banner) ? (
                <button
                  type="button"
                  className="block h-full w-full"
                  onClick={() => onSelect(banner)}
                >
                  {image}
                </button>
              ) : (
                image
              )}
            </div>
          );
        })}
      </div>
      {count > 1 && (
        <span className="pointer-events-none absolute bottom-2 right-2 rounded-full bg-black/40 px-2 py-0.5 text-xs font-medium text-white">
          {index + 1} / {count}
        </span>
      )}
    </div>
  );
};

/** 테스트 전용 — 모듈 캐시 초기화 */
export const resetGiftStoreBannerCache = () => bannerCache.clear();
